import { Injectable } from "@angular/core";
import { HttpClient, HttpEvent, HttpRequest } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError } from "rxjs/operators";
import { environment } from "../../../../../../environments/environment";
import { RequestTokenService } from "../../../../../../services/request-token.service";
import { DocumentRequestSignature } from "../../models/signature/DocumentRequestSignature";

const API_CADASTRO_URL = `${environment.apiUrl}v4/RequisicaoDocumento`;
const API_EXECUTIVO_URL = `${environment.apiExecutivoUrl}RequisicaoDocumento`;

@Injectable({
  providedIn: 'root'
})
export class RequisicaoDocumentoApiService {

  constructor(
    private http: HttpClient,
    private requestToken: RequestTokenService
  ) { }

  private handleError(error: any): Observable<never> {
    console.error('An error occurred', error);
    return throwError(error);
  }

  public GetTodasRequisicoes(): Observable<any> {
    const options = {
      headers: this.requestToken.createAuthorizationHeader(),
      withCredentials: true
    };

    return this.http.get<any>(`${API_CADASTRO_URL}/GetTodasRequisicoes`, options)
      .pipe(
        catchError(this.handleError)
      );
  }

  public GetRequisicoesPorIdCliente(idCliente: string): Observable<any> {
    const options = {
      headers: this.requestToken.createAuthorizationHeader(),
      withCredentials: true
    };

    return this.http.get<any>(`${API_EXECUTIVO_URL}/GetRequisicoesPorIdCliente?idCliente=${idCliente}`, options)
      .pipe(
        catchError(this.handleError)
      );
  }

  public GetRequisicaoPorId(id: string): Observable<any> {
    const options = {
      headers: this.requestToken.createAuthorizationHeader(),
      withCredentials: true
    };

    return this.http.get<any>(`${API_CADASTRO_URL}/GetRequisicaoPorId?id=${id}`, options)
      .pipe(
        catchError(this.handleError)
      );
  }

  public CriarRequisicao(signature: DocumentRequestSignature): Observable<any> {
    const formData = new FormData();

    // Append non-file properties as a JSON string
    const signatureWithoutFiles: any = { ...signature };
    delete signatureWithoutFiles.novosArquivosRequisicao;
    delete signatureWithoutFiles.novosArquivosEntregues;
    formData.append('signature', JSON.stringify(signatureWithoutFiles));

    // Append files for Requisicao
    if (signature.novosArquivosRequisicao && signature.novosArquivosRequisicao.length > 0) {
      signature.novosArquivosRequisicao.forEach((arquivo, index) => {
        if (arquivo.Conteudo) {
          formData.append(`novosArquivosRequisicao`, arquivo.Conteudo);
        }
      });
    }

    // Append files for Entregues - should never happen
    if (signature.novosArquivosEntregues && signature.novosArquivosEntregues.length > 0) {
      signature.novosArquivosEntregues.forEach((arquivo, index) => {
        if (arquivo.Conteudo) {
          formData.append(`novosArquivosEntregues`, arquivo.Conteudo);
        }
      });
    }

    const options = {
      headers: this.requestToken.createAuthorizationHeader(),
      withCredentials: true
    };

    // Remove Content-Type header to let the browser set it with the boundary
    if (options.headers.has('Content-Type')) {
      options.headers.delete('Content-Type');
    }

    return this.http.post<any>(`${API_EXECUTIVO_URL}/CriarRequisicao`, formData, options)
      .pipe(
        catchError(this.handleError)
      );
  }

  public CriarRequisicaoComProgresso(signature: DocumentRequestSignature): Observable<HttpEvent<any>> {
    const formData = new FormData();

    // Append non-file properties as a JSON string
    const signatureWithoutFiles: any = { ...signature };
    delete signatureWithoutFiles.novosArquivosRequisicao;
    delete signatureWithoutFiles.novosArquivosEntregues;
    formData.append('signature', JSON.stringify(signatureWithoutFiles));

    // Append files for Requisicao
    if (signature.novosArquivosRequisicao && signature.novosArquivosRequisicao.length > 0) {
      signature.novosArquivosRequisicao.forEach((arquivo, index) => {
        if (arquivo.Conteudo) {
          formData.append(`novosArquivosRequisicao`, arquivo.Conteudo);
        }
      });
    }

    // Append files for Entregues - should never happen
    if (signature.novosArquivosEntregues && signature.novosArquivosEntregues.length > 0) {
      signature.novosArquivosEntregues.forEach((arquivo, index) => {
        if (arquivo.Conteudo) {
          formData.append(`novosArquivosEntregues`, arquivo.Conteudo);
        }
      });
    }

    const headers = this.requestToken.createAuthorizationHeader();

    // Remove Content-Type header to let the browser set it with the boundary
    if (headers.has('Content-Type')) {
      headers.delete('Content-Type');
    }

    const req = new HttpRequest('POST', `${API_EXECUTIVO_URL}/CriarRequisicao`, formData, {
      headers: headers,
      withCredentials: true,
      reportProgress: true
    });

    return this.http.request(req)
      .pipe(
        catchError(this.handleError)
      );
  }

  public ModificarRequisicao(signature: DocumentRequestSignature): Observable<any> {
    const formData = new FormData();

    // Append non-file properties as a JSON string
    const signatureWithoutFiles: any = { ...signature };
    delete signatureWithoutFiles.novosArquivosRequisicao;
    delete signatureWithoutFiles.novosArquivosEntregues;
    formData.append('signature', JSON.stringify(signatureWithoutFiles));

    // Append files for Requisicao
    if (signature.novosArquivosRequisicao && signature.novosArquivosRequisicao.length > 0) {
      signature.novosArquivosRequisicao.forEach((arquivo, index) => {
        if (arquivo.Conteudo) {
          formData.append(`novosArquivosRequisicao`, arquivo.Conteudo);
        }
      });
    }

    // Append files for Entregues
    if (signature.novosArquivosEntregues && signature.novosArquivosEntregues.length > 0) {
      signature.novosArquivosEntregues.forEach((arquivo, index) => {
        if (arquivo.Conteudo) {
          formData.append(`novosArquivosEntregues`, arquivo.Conteudo);
        }
      });
    }

    const options = {
      headers: this.requestToken.createAuthorizationHeader(),
      withCredentials: true
    };

    // Remove Content-Type header to let the browser set it with the boundary
    if (options.headers.has('Content-Type')) {
      options.headers.delete('Content-Type');
    }

    return this.http.put<any>(`${API_EXECUTIVO_URL}/ModificarRequisicao`, formData, options)
      .pipe(
        catchError(this.handleError)
      );
  }

  public ModificarRequisicaoComProgresso(signature: DocumentRequestSignature): Observable<HttpEvent<any>> {
    const formData = new FormData();

    // Append non-file properties as a JSON string
    const signatureWithoutFiles: any = { ...signature };
    delete signatureWithoutFiles.novosArquivosRequisicao;
    delete signatureWithoutFiles.novosArquivosEntregues;
    formData.append('signature', JSON.stringify(signatureWithoutFiles));

    // Append files for Requisicao
    if (signature.novosArquivosRequisicao && signature.novosArquivosRequisicao.length > 0) {
      signature.novosArquivosRequisicao.forEach((arquivo, index) => {
        if (arquivo.Conteudo) {
          formData.append(`novosArquivosRequisicao`, arquivo.Conteudo);
        }
      });
    }

    // Append files for Entregues
    if (signature.novosArquivosEntregues && signature.novosArquivosEntregues.length > 0) {
      signature.novosArquivosEntregues.forEach((arquivo, index) => {
        if (arquivo.Conteudo) {
          formData.append(`novosArquivosEntregues`, arquivo.Conteudo);
        }
      });
    }

    const headers = this.requestToken.createAuthorizationHeader();

    // Remove Content-Type header to let the browser set it with the boundary
    if (headers.has('Content-Type')) {
      headers.delete('Content-Type');
    }

    const req = new HttpRequest('PUT', `${API_EXECUTIVO_URL}/ModificarRequisicao`, formData, {
      headers: headers,
      withCredentials: true,
      reportProgress: true
    });

    return this.http.request(req)
      .pipe(
        catchError(this.handleError)
      );
  }

  public ExcluirRequisicao(id: string): Observable<any> {
    const options = {
      headers: this.requestToken.createAuthorizationHeader(),
      withCredentials: true
    };

    return this.http.delete<any>(`${API_EXECUTIVO_URL}/ExcluirRequisicao?id=${id}`, options)
      .pipe(
        catchError(this.handleError)
      );
  }

  public BaixarArquivoSolicitacao(nomeArquivo: string): Observable<any> {
    const options = {
      headers: this.requestToken.createAuthorizationHeader(),
      withCredentials: true
    };

    return this.http.get<any>(`${API_CADASTRO_URL}/BaixarArquivoSolicitacao?nomeArquivo=${nomeArquivo}`, options)
      .pipe(
        catchError(this.handleError)
      );
  }

  public ObterUrlDownloadArquivo(nomeArquivo: string, minutosValidade: number = 60): Observable<any> {
    const options = {
      headers: this.requestToken.createAuthorizationHeader(),
      withCredentials: true
    };

    return this.http.get<any>(
      `${API_CADASTRO_URL}/ObterUrlDownloadArquivo?nomeArquivo=${encodeURIComponent(nomeArquivo)}&minutosValidade=${minutosValidade}`,
      options
    ).pipe(
      catchError(this.handleError)
    );
  }

  public MarcarComoLido(id: string): Observable<any> {
    const options = {
      headers: this.requestToken.createAuthorizationHeader(),
      withCredentials: true
    };

    // Assuming a PATCH request is suitable for marking as read
    return this.http.patch<any>(`${API_CADASTRO_URL}/MarcarComoLido/${id}`, null, options)
      .pipe(
        catchError(this.handleError)
      );
  }
} 